import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const questions = [
  {
    id: 1,
    title: "THE AMBITION TEST",
    text: "Toronto has thousands of casual paddlers, but only a few ever leave the shore. Are you ready to move beyond the beach and learn the technical skills required to command the open waters of Lake Ontario?"
  },
  {
    id: 2,
    title: "THE GEAR & SAFETY COMMITMENT",
    text: "Do you agree that when operating in a high-traffic maritime environment, having professional-grade, high-visibility gear isn't about the look—it's about the safety and discipline of the entire Syndicate?"
  },
  {
    id: 3,
    title: "THE GROWTH MINDSET",
    text: "OWS is built for operators who are eager to learn and refuse to settle for 'average.' If we provide the training and the hardware, are you ready to show up with the discipline to master it?"
  }
];

export default function DeploymentClearance() {
  const [step, setStep] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();

  const handleYes = () => {
    if (step < questions.length - 1) {
      setStep(prev => prev + 1);
    } else {
      setAgreed(true);
      setTimeout(() => {
        navigate('/syndicate');
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-ows-dark text-white flex flex-col items-center justify-center relative overflow-hidden py-32 px-4">
      <Helmet>
        <title>Deployment Clearance | Open Water Syndicate</title>
      </Helmet>
      
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-ows-teal blur-[150px] rounded-full mix-blend-screen" />
      </div>

      <div className="max-w-2xl w-full z-10 relative">
        <AnimatePresence mode="wait">
          {!agreed ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
              transition={{ duration: 0.5 }}
              className="bg-white/5 border border-white/10 p-10 md:p-16 rounded-[2rem] backdrop-blur-sm"
            >
              <div className="flex items-center gap-4 mb-10">
                <Shield className="h-8 w-8 text-ows-teal" />
                <span className="text-xs font-mono font-bold uppercase tracking-[0.3em] text-ows-teal">
                  Clearance Check {step + 1}/3
                </span>
              </div>
              
              <h2 className="text-2xl md:text-3xl font-display font-light uppercase tracking-widest mb-6">
                {questions[step].title}
              </h2>
              
              <p className="text-lg md:text-xl font-light text-white/80 leading-relaxed font-serif mb-12">
                "{questions[step].text}"
              </p>

              <button
                onClick={handleYes}
                className="w-full py-6 bg-ows-teal text-white hover:bg-white hover:text-ows-dark transition-all duration-500 rounded-2xl font-mono font-bold text-sm tracking-[0.4em] uppercase flex items-center justify-center gap-3 group"
              >
                YES <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="cleared"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-24 h-24 bg-ows-teal/20 rounded-full flex items-center justify-center mx-auto mb-8 relative">
                <div className="absolute inset-0 border border-ows-teal rounded-full animate-ping opacity-20" />
                <Check className="h-10 w-10 text-ows-teal" />
              </div>
              
              <h2 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-white mb-6">
                CLEARANCE GRANTED.
              </h2>
              
              <p className="text-xl md:text-2xl text-ows-teal font-light uppercase tracking-[0.2em] mb-12">
                YOU HAVE THE MINDSET.<br />NOW CHOOSE YOUR VESSEL.
              </p>
              
              <div className="flex justify-center">
                <div className="h-1 w-1 bg-white/20 rounded-full animate-pulse mr-2" />
                <div className="h-1 w-1 bg-white/20 rounded-full animate-pulse delay-75 mr-2" />
                <div className="h-1 w-1 bg-white/20 rounded-full animate-pulse delay-150" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
